import { forwardRef, useEffect, useImperativeHandle, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';

export interface SignaturePadHandle {
  clear: () => void;
  isEmpty: () => boolean;
  toDataURL: () => string;
}

interface SignaturePadProps {
  height?: number;
  /** When set, a clear button with this label sits inside the pad's top-right corner. */
  clearLabel?: string;
}

export const SignaturePad = forwardRef<SignaturePadHandle, SignaturePadProps>(
  ({ height = 160, clearLabel }, ref) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const drawingRef = useRef(false);
    const hasDrawnRef = useRef(false);
    const lastPointRef = useRef<{ x: number; y: number } | null>(null);
    const [hasInk, setHasInk] = useState(false);

    const clear = () => {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (!canvas || !ctx) return;
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.restore();
      hasDrawnRef.current = false;
      setHasInk(false);
    };

    useImperativeHandle(ref, () => ({
      clear,
      isEmpty: () => !hasDrawnRef.current,
      toDataURL: () => canvasRef.current?.toDataURL('image/png') ?? '',
    }));

    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      // Render at device pixel ratio so strokes stay crisp, but keep the logical drawing
      // surface in CSS pixels so pointer coordinates map 1:1 without extra scaling math.
      const setup = () => {
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const dpr = window.devicePixelRatio || 1;
        const cssWidth = canvas.clientWidth || 320;
        const targetW = Math.round(cssWidth * dpr);
        const targetH = Math.round(height * dpr);
        if (canvas.width === targetW && canvas.height === targetH) return;
        // Keep any signature already drawn when the phone rotates / the layout resizes.
        let previous: HTMLCanvasElement | null = null;
        if (hasDrawnRef.current && canvas.width && canvas.height) {
          previous = document.createElement('canvas');
          previous.width = canvas.width;
          previous.height = canvas.height;
          previous.getContext('2d')?.drawImage(canvas, 0, 0);
        }
        canvas.width = targetW;
        canvas.height = targetH;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, cssWidth, height);
        if (previous) {
          ctx.drawImage(previous, 0, 0, previous.width / dpr, previous.height / dpr);
        }
        ctx.strokeStyle = '#111';
        ctx.lineWidth = 2.5;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
      };
      setup();
      let observer: ResizeObserver | null = null;
      if (typeof ResizeObserver !== 'undefined') {
        observer = new ResizeObserver(() => setup());
        observer.observe(canvas);
      } else {
        window.addEventListener('resize', setup);
      }
      return () => {
        observer?.disconnect();
        window.removeEventListener('resize', setup);
      };
    }, [height]);

    const getPoint = (e: ReactPointerEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current!;
      const rect = canvas.getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const handlePointerDown = (e: ReactPointerEvent<HTMLCanvasElement>) => {
      drawingRef.current = true;
      lastPointRef.current = getPoint(e);
      canvasRef.current?.setPointerCapture(e.pointerId);
    };

    const handlePointerMove = (e: ReactPointerEvent<HTMLCanvasElement>) => {
      if (!drawingRef.current) return;
      const ctx = canvasRef.current?.getContext('2d');
      if (!ctx || !lastPointRef.current) return;
      const point = getPoint(e);
      ctx.beginPath();
      ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
      ctx.lineTo(point.x, point.y);
      ctx.stroke();
      lastPointRef.current = point;
      if (!hasDrawnRef.current) {
        hasDrawnRef.current = true;
        setHasInk(true);
      }
    };

    const handlePointerUp = () => {
      drawingRef.current = false;
      lastPointRef.current = null;
    };

    return (
      <div className="signature-pad">
        {!hasInk && (
          <span className="signature-placeholder" aria-hidden>
            Sign here
          </span>
        )}
        <canvas
          ref={canvasRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
          className="signature-canvas"
          style={{ height }}
          aria-label="Signature box – sign with your finger"
          role="img"
        />
        {clearLabel && (
          <button type="button" className="signature-clear" onClick={clear}>
            {clearLabel}
          </button>
        )}
        <style>{`
          .signature-pad { position: relative; }
          .signature-placeholder {
            position: absolute;
            inset: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #9aa0a6;
            font-size: 1.25rem;
            pointer-events: none;
            user-select: none;
          }
          .signature-clear {
            position: absolute;
            top: 8px;
            right: 8px;
            min-height: 44px;
            padding: 0 14px;
            border-radius: 8px;
            border: 1px solid #5B6670;
            background: #E9EDF1;
            color: #0E1216;
            font-weight: 600;
            font-size: 0.9375rem;
            cursor: pointer;
          }
          .signature-canvas {
            display: block;
            width: 100%;
            border: 1px solid var(--color-border);
            border-radius: var(--radius-md);
            background: #fff;
            touch-action: none;
            cursor: crosshair;
          }
        `}</style>
      </div>
    );
  },
);

SignaturePad.displayName = 'SignaturePad';
